import { SugerenciaCita } from '../../../../services/sugerenciaCitaService.ts';

export const MAX_SUGERENCIAS_VISIBLES = 3;

export interface IdentidadFeed {
    userId: number | undefined;
    generation: number;
}

export const siguienteIdentidadFeed = (
    actual: IdentidadFeed,
    userId: number | undefined,
): IdentidadFeed => actual.userId === userId
    ? actual
    : { userId, generation: actual.generation + 1 };

export const identidadFeedCoincide = (
    esperada: IdentidadFeed,
    actual: IdentidadFeed,
): boolean =>
    esperada.userId === actual.userId &&
    esperada.generation === actual.generation;

const claveServicio = (sugerencia: SugerenciaCita): string => {
    if (sugerencia.servicio_id !== null && sugerencia.servicio_id !== undefined) {
        return `id:${sugerencia.servicio_id}`;
    }
    return `nombre:${(sugerencia.servicio_sugerido_nombre || '').trim().toLocaleLowerCase('es')}`;
};

/**
 * Defensa de presentación para datos antiguos: el backend genera servicios
 * distintos, pero una base previa puede conservar sugerencias duplicadas.
 */
export const normalizarSugerenciasDiversas = (
    sugerencias: SugerenciaCita[],
    limite = MAX_SUGERENCIAS_VISIBLES,
): SugerenciaCita[] => {
    const servicios = new Set<string>();
    const resultado: SugerenciaCita[] = [];

    for (const sugerencia of sugerencias) {
        const clave = claveServicio(sugerencia);
        if (servicios.has(clave)) continue;
        servicios.add(clave);
        resultado.push(sugerencia);
        if (resultado.length >= limite) break;
    }

    return resultado;
};

/** Mantiene cada tarjeta en su slot y jamás introduce un servicio repetido. */
export const fusionarReemplazoDiverso = (
    anteriores: SugerenciaCita[],
    idsAEliminar: number[],
    nuevas: SugerenciaCita[],
    limite = MAX_SUGERENCIAS_VISIBLES,
): SugerenciaCita[] => {
    const idsEliminados = new Set(idsAEliminar);
    const idsVigentes = new Set(nuevas.map((item) => item.id));
    const idsAnteriores = new Set(anteriores.map((item) => item.id));
    const retenidas = anteriores.filter(
        (item) => !idsEliminados.has(item.id) && idsVigentes.has(item.id),
    );
    const serviciosRetenidos = new Set(retenidas.map(claveServicio));
    const serviciosDescartados = new Set(
        anteriores.filter((item) => idsEliminados.has(item.id)).map(claveServicio),
    );
    const serviciosCandidatos = new Set<string>();
    const reemplazosPreferidos: SugerenciaCita[] = [];
    const reemplazosReciclados: SugerenciaCita[] = [];
    for (const item of nuevas) {
        const clave = claveServicio(item);
        if (
            idsAnteriores.has(item.id)
            || serviciosRetenidos.has(clave)
            || serviciosCandidatos.has(clave)
        ) continue;
        serviciosCandidatos.add(clave);
        if (serviciosDescartados.has(clave)) {
            reemplazosReciclados.push(item);
        } else {
            reemplazosPreferidos.push(item);
        }
    }
    // El feedback negativo ordena al final, no elimina para siempre. Si el
    // catálogo está agotado, reciclar un servicio no visible evita un feed vacío.
    const reemplazos = [...reemplazosPreferidos, ...reemplazosReciclados];

    const resultado: SugerenciaCita[] = [];
    const serviciosResultado = new Set<string>();
    for (const anterior of anteriores) {
        let candidato: SugerenciaCita | undefined;
        if (idsEliminados.has(anterior.id)) {
            candidato = reemplazos.shift();
        } else if (idsVigentes.has(anterior.id)) {
            candidato = anterior;
        }
        if (!candidato) continue;
        const clave = claveServicio(candidato);
        if (serviciosResultado.has(clave)) continue;
        serviciosResultado.add(clave);
        resultado.push(candidato);
    }

    for (const reemplazo of reemplazos) {
        if (resultado.length >= limite) break;
        const clave = claveServicio(reemplazo);
        if (serviciosResultado.has(clave)) continue;
        serviciosResultado.add(clave);
        resultado.push(reemplazo);
    }

    const clavesRecicladas = new Set(reemplazosReciclados.map(claveServicio));
    const ordenadas = [
        ...resultado.filter((item) => !clavesRecicladas.has(claveServicio(item))),
        ...resultado.filter((item) => clavesRecicladas.has(claveServicio(item))),
    ];
    return ordenadas.slice(0, limite);
};
